<template>
  <Accordion
    :multiple="false"
    :value="accordionValue"
    class="single-accordion"
    @update:value="onAccordionValueUpdate"
    @tab-open="onOpen"
    @tab-close="onClose"
  >
    <AccordionPanel value="main">
      <AccordionHeader @click="markInteracted">
        <span class="single-accordion-header-content">
          <FileKindIcon
            v-if="headerFileKind"
            :fileKind="headerFileKind"
            class="single-accordion-file-icon"
          />
          <span>{{ title }}</span>
        </span>
      </AccordionHeader>
      <AccordionContent>
        <slot v-if="!lazy || hasInteracted" />
      </AccordionContent>
    </AccordionPanel>
  </Accordion>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import Accordion from 'primevue/accordion';

import FileKindIcon from '@/components/files/FileKindIcon.vue';
import { type FileKind, getFileKindFromFilename } from '@/utils/files';

const props = withDefaults(
  defineProps<{
    open?: boolean;
    defaultOpen?: boolean;
    title?: string;
    fileNameForIcon?: string;
    disabled?: boolean;
    lazy?: boolean;
  }>(),
  {
    defaultOpen: false,
    title: '',
    fileNameForIcon: '',
    disabled: false,
    lazy: false,
  },
);

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
  (e: 'open'): void;
  (e: 'close'): void;
}>();

const isControlled = computed(() => props.open !== undefined);
const innerOpen = ref<boolean>(props.defaultOpen);
const hasInteracted = ref<boolean>(!!props.defaultOpen || !!props.open);

watch(
  () => props.defaultOpen,
  (v) => {
    if (!isControlled.value) innerOpen.value = !!v;
    if (v) hasInteracted.value = true;
  },
);

watch(
  () => props.open,
  (v) => {
    if (v) hasInteracted.value = true;
  },
);

const isOpen = computed<boolean>({
  get() {
    return isControlled.value ? !!props.open : innerOpen.value;
  },
  set(v) {
    if (props.disabled) return;
    if (isControlled.value) emit('update:open', v);
    else innerOpen.value = v;
  },
});

const accordionValue = computed(() => (isOpen.value ? 'main' : null));

const headerFileKind = computed<FileKind | null>(() => {
  const fileKind = getFileKindFromFilename(props.fileNameForIcon);
  return fileKind ?? null;
});

function onAccordionValueUpdate(value: string | string[] | null | undefined) {
  if (props.disabled) return;

  const nextIsOpen = Array.isArray(value) ? value.includes('main') : value === 'main';
  const prevIsOpen = isOpen.value;

  if (nextIsOpen) {
    hasInteracted.value = true;
  }

  isOpen.value = nextIsOpen;

  if (nextIsOpen && !prevIsOpen) {
    emit('open');
  }

  if (!nextIsOpen && prevIsOpen) {
    emit('close');
  }
}

function onOpen() {
  if (props.disabled) return;
  const prevIsOpen = isOpen.value;
  hasInteracted.value = true;
  isOpen.value = true;
  if (!prevIsOpen) {
    emit('open');
  }
}

function markInteracted() {
  if (props.disabled) return;
  hasInteracted.value = true;
}

function onClose() {
  if (props.disabled) return;
  const prevIsOpen = isOpen.value;
  isOpen.value = false;
  if (prevIsOpen) {
    emit('close');
  }
}
</script>

<style scoped lang="css">
.single-accordion-header-content {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.single-accordion :deep(.p-accordionpanel) {
  border: none;
  background: transparent;
}

.single-accordion-file-icon {
  flex-shrink: 0;
}

.single-accordion-loading-icon {
  font-size: 0.95rem;
  opacity: 0.9;
}

.single-accordion :deep(.p-accordionheader) {
  background: transparent;
  border: none;
  color: var(--p-text-color);
  transition:
    color 0.2s ease,
    text-decoration-color 0.2s ease;
}

.single-accordion :deep(.p-accordionheader:hover),
.single-accordion :deep(.p-accordionheader:focus-visible) {
  color: var(--p-primary-color);
}

.single-accordion :deep(.p-accordionheader[data-p-active='true']) {
  color: var(--p-text-color);
}
</style>
